import { Ear, Headphones } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import VoicePicker from "@/edgar/VoicePicker";
import { speak } from "@/edgar/voice";

// Hands-free controls under Edgar's header: on/off, keep listening vs tap mic, and the "Hey Edgar" wake word.
export default function VoiceSettings({ prefs, setPrefs, supported }) {
  const set = (k, v) => setPrefs({ ...prefs, [k]: v });
  if (!supported) {
    return <p className="border-b border-slate-100 px-4 py-2 text-[11px] text-slate-500" data-testid="edgar-voice-unsupported">Hands-free needs Chrome, Edge or Safari.</p>;
  }
  const seg = (v, label) => (
    <button type="button" onClick={() => set("loop", v)} data-testid={`edgar-loop-${v ? "on" : "off"}`}
      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold transition-colors ${prefs.loop === v ? "bg-slate-900 text-white" : "text-slate-600"}`}>{label}</button>
  );
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2" data-testid="edgar-voice-settings">
      <button type="button" onClick={() => set("on", !prefs.on)} aria-pressed={prefs.on} data-testid="edgar-hands-free"
        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${prefs.on ? "bg-[#0053DA] text-white" : "border border-slate-300 text-slate-700 hover:border-[#0053DA]"}`}>
        <Headphones className="h-3.5 w-3.5" /> Hands-free {prefs.on ? "on" : "off"}
      </button>
      {prefs.on && <span className="inline-flex rounded-full border border-slate-200 p-0.5">{seg(true, "Keep listening")}{seg(false, "Tap mic")}</span>}
      {prefs.on && <VoicePicker />}
      {prefs.on && (
        <label className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600" data-testid="edgar-speed-label">
          Speed
          <input type="range" min="0.7" max="1.6" step="0.1" value={prefs.rate || 1} onChange={(e) => set("rate", Number(e.target.value))}
            onPointerUp={() => speak("This is how fast I'll talk.")} className="h-1 w-20 accent-[#0053DA]" data-testid="edgar-speed" aria-label="Edgar's talking speed" />
          <span className="w-8 font-mono text-slate-500" data-testid="edgar-speed-value">{(prefs.rate || 1).toFixed(1)}×</span>
        </label>
      )}
      <label className="flex w-full cursor-pointer items-center gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5" data-testid="edgar-wake-label">
        <Ear className="h-3.5 w-3.5 shrink-0 text-[#0053DA]" />
        <span className="min-w-0 flex-1">
          <span className="block text-[11px] font-semibold text-slate-800">Listen for &ldquo;Hey Edgar&rdquo;</span>
          <span className="block text-[10px] text-slate-500" data-testid="edgar-wake-hint">{prefs.wakeOn ? "On: tap the Mic badge in the corner once to start listening. It starts by itself if your browser already allows the mic." : "Off: the microphone is only used when you tap the mic."}</span>
        </span>
        <Switch checked={!!prefs.wakeOn} onCheckedChange={(v) => set("wakeOn", v)} aria-label="Listen for Hey Edgar" data-testid="edgar-wake" />
      </label>
      <button type="button" onClick={() => window.dispatchEvent(new Event("snaplist:edgar-pocket"))} data-testid="edgar-pocket-toggle"
        className="inline-flex items-center gap-1 rounded-full border border-slate-900 px-2.5 py-1 text-[11px] font-semibold text-slate-900 transition-colors hover:bg-slate-900 hover:text-white">
        <Headphones className="h-3.5 w-3.5" /> Headphone mode
      </button>
    </div>
  );
}
